import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

/**
 * Sticky in-app "Back" pill.
 *
 * The browser back button on some deep-linked pages sends the user all the
 * way to the landing page (auth redirects + history rewrites eat the
 * intermediate stack). This gives users a reliable way to step back one
 * screen without depending on the browser stack.
 *
 * Placement: rendered globally from AppRouter. Auto-hides on:
 *   - the landing page (nothing to go back to)
 *   - the auth callback (breaks the OAuth handoff)
 *   - the onboarding flow (has its own step navigation)
 */
const HIDE_ON = new Set(["/", "/auth"]);

export default function BackButton() {
  const location = useLocation();
  const navigate = useNavigate();
  if (HIDE_ON.has(location.pathname)) return null;

  const goBack = () => {
    // If the user opened this URL as their first tab, `window.history.length`
    // will be 1 — `navigate(-1)` would leave the app. Fall back to studio (for
    // signed-in users the platform will re-check auth) or landing.
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <button
      type="button"
      onClick={goBack}
      data-testid="global-back-btn"
      aria-label="Go back to the previous page"
      style={{
        position: "fixed",
        top: 76,
        left: 20,
        zIndex: 40,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 14px 7px 10px",
        borderRadius: 999,
        border: "1px solid var(--v-border)",
        background: "rgba(10,10,10,0.72)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        color: "var(--v-text)",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        cursor: "pointer",
        transition: "border-color .2s ease, color .2s ease, background .2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--v-accent)";
        e.currentTarget.style.color = "var(--v-accent)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--v-border)";
        e.currentTarget.style.color = "var(--v-text)";
      }}
    >
      <ChevronLeft size={14} />
      Back
    </button>
  );
}
